import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useStore } from '../store/useStore';
import {
  Smartphone, Save,
  Loader2, AlertCircle, Send,
  Check, Shield, Zap, Plus, Trash2, Key, Calendar
} from 'lucide-react';
import { sendSMS } from '../lib/sms-service';
import { GoogleCalendarService } from '../lib/google-calendar';
import { GoogleCalendarConnect } from '../components/GoogleCalendarConnect';
import { SetupWizard, type TwilioSetupData, type AISetupData } from '../components/SetupWizard';

export default function SMSSettings() {
  const [phone, setPhone] = useState('');
  const [carrier, setCarrier] = useState('Auto-Detect');
  const [autoReply, setAutoReply] = useState(false);
  const [autoReplyMessage, setAutoReplyMessage] = useState('Thanks for your message! I will get back to you soon.');
  
  // Advanced AI Responder Settings
  const [aiResponderEnabled, setAiResponderEnabled] = useState(false);
  const [aiResponderTone, setAiResponderTone] = useState('Professional');
  const [aiResponderPrompt, setAiResponderPrompt] = useState('');
  const [aiConfirmBeforeSending, setAiConfirmBeforeSending] = useState(false);
  const [aiNoScheduling, setAiNoScheduling] = useState(false);
  const [aiExplicitOnly, setAiExplicitOnly] = useState(false);
  const [aiCustomRules, setAiCustomRules] = useState('');

  // Twilio Settings
  const [twilioSid, setTwilioSid] = useState('');
  const [twilioToken, setTwilioToken] = useState('');
  const [twilioNumbers, setTwilioNumbers] = useState<string[]>([]);
  const [newTwilioNumber, setNewTwilioNumber] = useState('');

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [hasGmailPerm, setHasGmailPerm] = useState<boolean | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showTwilioWizard, setShowTwilioWizard] = useState(false);
  const [showAIWizard, setShowAIWizard] = useState(false);

  const { currentUser } = useStore();

  useEffect(() => {
    async function loadPreferences() {
      if (!currentUser?.id) {
        return;
      }

      // Check Gmail Permission
      const googleService = GoogleCalendarService.getInstance();
      const hasPerm = await googleService.hasRequiredPermissions(currentUser.id);
      setHasGmailPerm(hasPerm);

      if (isSupabaseConfigured && supabase) {
        try {
          const { data } = await supabase
            .from('agent_preferences')
            .select('*')
            .eq('user_id', currentUser.id)
            .maybeSingle();

          if (data) {
            setPhone(data.phone_number || '');
            setCarrier(data.carrier || 'Auto-Detect');
            setAutoReply(!!data.sms_auto_reply_enabled);
            if (data.sms_auto_reply_message) setAutoReplyMessage(data.sms_auto_reply_message);
            
            // Advanced AI
            setAiResponderEnabled(!!data.sms_auto_responder_enabled);
            setAiResponderTone(data.sms_auto_responder_tone || 'Professional');
            setAiResponderPrompt(data.sms_auto_responder_custom_prompt || '');
            setAiConfirmBeforeSending(!!data.sms_ai_confirm_before_sending);
            setAiNoScheduling(!!data.sms_ai_no_scheduling);
            setAiExplicitOnly(!!data.sms_ai_explicit_only);
            setAiCustomRules(data.sms_ai_custom_rules || '');

            // Twilio
            setTwilioSid(data.twilio_account_sid || '');
            setTwilioToken(data.twilio_auth_token || '');
            setTwilioNumbers(Array.isArray(data.twilio_phone_numbers) ? data.twilio_phone_numbers : []);
          }
        } catch (err) {
          console.error('Failed to load SMS preferences:', err);
        }
      }
    }

    loadPreferences();
  }, [currentUser]);

  const handleTestSMS = async () => {
    if (!phone) return;
    setTesting(true);
    setTestResult(null);

    const testMessage = `WholeScale OS: Your SMS connection is working! Source: ${twilioSid ? 'Twilio' : 'Gmail Gateway'}`;
    
    try {
      const res = await sendSMS(phone, testMessage, { 
        carrier, 
        userId: currentUser?.id,
        fromNumber: twilioNumbers[0] 
      });

      if (res.success) {
        setTestResult({ success: true, message: res.message });
      } else {
        setTestResult({ success: false, message: `Failed: ${res.message}` });
      }
    } catch (err) {
      setTestResult({ success: false, message: 'Failed to send test SMS. Check your connection settings.' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser?.id) return;
    setSaving(true);
    setSaveResult(null);

    if (isSupabaseConfigured && supabase) {
      try {
        const { error } = await supabase
          .from('agent_preferences')
          .upsert(
            {
              user_id: currentUser.id,
              phone_number: phone,
              carrier: carrier,
              sms_auto_reply_enabled: autoReply,
              sms_auto_reply_message: autoReplyMessage,
              sms_auto_responder_enabled: aiResponderEnabled,
              sms_auto_responder_tone: aiResponderTone,
              sms_auto_responder_custom_prompt: aiResponderPrompt,
              sms_ai_confirm_before_sending: aiConfirmBeforeSending,
              sms_ai_no_scheduling: aiNoScheduling,
              sms_ai_explicit_only: aiExplicitOnly,
              sms_ai_custom_rules: aiCustomRules,
              twilio_account_sid: twilioSid,
              twilio_auth_token: twilioToken,
              twilio_phone_numbers: twilioNumbers,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'user_id' }
          );

        if (error) throw error;
        setSaveResult({ success: true, message: 'All SMS settings saved successfully.' });
      } catch (err: any) {
        setSaveResult({ success: false, message: `Failed to save: ${err.message}` });
      }
    }
    setSaving(false);
  };

  const addTwilioNumber = () => {
    if (!newTwilioNumber) return;
    const clean = newTwilioNumber.replace(/[^\d]/g, '');
    const formatted = clean.length === 10 ? `+1${clean}` : `+${clean}`;
    if (!twilioNumbers.includes(formatted)) {
      setTwilioNumbers([...twilioNumbers, formatted]);
    }
    setNewTwilioNumber('');
  };

  const removeTwilioNumber = (num: string) => {
    setTwilioNumbers(twilioNumbers.filter(n => n !== num));
  };


  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">SMS & AI Communication</h1>
        <p className="text-[var(--t-text-muted)]">Configure multi-number management and AI-powered auto-responders.</p>
      </div>

      <div className="flex flex-col gap-6">
        
        {/* Left Column: Core Setup & Gateway */}
        <div className="space-y-6">
          <div className="bg-[var(--t-surface)] rounded-2xl border border-[var(--t-border)] p-6 space-y-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center border bg-[var(--t-primary-dim)] border-[var(--t-primary-dim)]">
                <Smartphone className="w-5 h-5 text-[var(--t-primary)]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">SMS Gateway Setup</h2>
                <p className="text-xs text-[var(--t-text-muted)]">Default Gmail-SMS delivery parameters.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1.5 text-[var(--t-text-muted)]">Personal Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full rounded-xl px-4 py-2.5 bg-[var(--t-background)] border border-[var(--t-border)] text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5 text-[var(--t-text-muted)]">Carrier Domain</label>
                <select
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="w-full rounded-xl px-4 py-2.5 bg-[var(--t-background)] border border-[var(--t-border)] text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all cursor-pointer"
                >
                  <option value="Auto-Detect">Auto-Detect (Blast)</option>
                  <option value="T-Mobile">T-Mobile / Boost / Metro</option>
                  <option value="Verizon">Verizon / Visible</option>
                  <option value="Google Fi">Google Fi</option>
                </select>
              </div>
            </div>

            {hasGmailPerm === false ? (
              <div className="p-4 rounded-xl border border-[var(--t-warning-border)] bg-[var(--t-warning-dim)] flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[var(--t-warning)] shrink-0" />
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[var(--t-warning)]">Gmail Connection Missing</p>
                  <p className="text-xs text-[var(--t-warning)] opacity-90">Required for free gateway delivery.</p>
                  <GoogleCalendarConnect />
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-xl border border-[var(--t-success-border)] bg-[var(--t-success-dim)] flex items-center justify-between">
                <div className="flex items-center gap-2 text-[var(--t-success)] text-sm font-medium">
                  <Check className="w-4 h-4" /> Gmail Gateway Active
                </div>
                <GoogleCalendarConnect />
              </div>
            )}
          </div>

          {/* Twilio BYO Card */}
          <div className="bg-[var(--t-surface)] rounded-2xl border border-[var(--t-border)] p-6 space-y-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center border bg-[var(--t-info-dim)] border-[var(--t-info-dim)]">
                  <Key className="w-5 h-5 text-[var(--t-info)]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Twilio (Premium)</h2>
                  <p className="text-xs text-[var(--t-text-muted)]">Native SMS delivery with unique numbers.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowTwilioWizard(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--t-info-dim)] text-[var(--t-info)] text-[10px] font-bold uppercase tracking-widest rounded-lg hover:bg-[var(--t-info)] hover:text-black transition-all"
              >
                <Zap className="w-3 h-3" /> Launch Setup Wizard
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-[var(--t-text-muted)]">Account SID</label>
                  <input
                    type="password"
                    value={twilioSid}
                    onChange={(e) => setTwilioSid(e.target.value)}
                    placeholder="AC..."
                    className="w-full rounded-xl px-4 py-2.5 bg-[var(--t-background)] border border-[var(--t-border)] text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-[var(--t-text-muted)]">Auth Token</label>
                  <input
                    type="password"
                    value={twilioToken}
                    onChange={(e) => setTwilioToken(e.target.value)}
                    placeholder="Auth Token"
                    className="w-full rounded-xl px-4 py-2.5 bg-[var(--t-background)] border border-[var(--t-border)] text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all font-mono text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5 text-[var(--t-text-muted)]">Managed Numbers</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newTwilioNumber}
                    onChange={(e) => setNewTwilioNumber(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    className="flex-1 rounded-xl px-4 py-2.5 bg-[var(--t-background)] border border-[var(--t-border)] text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all"
                  />
                  <button
                    onClick={addTwilioNumber}
                    className="p-2.5 rounded-xl bg-[var(--t-primary)] text-white hover:opacity-90 transition-all shadow-lg"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-2">
                  {twilioNumbers.map(num => (
                    <div key={num} className="flex items-center justify-between p-3 rounded-xl bg-[var(--t-background)] border border-[var(--t-border)] group">
                      <span className="text-sm text-white font-medium">{num}</span>
                      <button onClick={() => removeTwilioNumber(num)} className="text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {twilioNumbers.length === 0 && (
                    <p className="text-[10px] text-center text-[var(--t-text-muted)] py-4 border border-dashed rounded-xl border-[var(--t-border)]">No Twilio numbers added.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Auto-Responder Section */}
        <div className="space-y-6">
          <div className="bg-[var(--t-surface)] rounded-2xl border border-[var(--t-border)] p-6 space-y-6 shadow-xl relative overflow-hidden animate-none">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center border bg-[var(--t-success-dim)] border-[var(--t-success-dim)]">
                  <Zap className="w-5 h-5 text-[var(--t-success)]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">AI Auto-Responder</h2>
                  <p className="text-xs text-[var(--t-text-muted)]">Automatic contextual replies to leads.</p>
                </div>
              </div>
              <div 
                onClick={() => setAiResponderEnabled(!aiResponderEnabled)}
                className={`toggle-switch ${aiResponderEnabled ? 'is-on' : 'is-off'}`}
              >
                <div className="toggle-knob" />
              </div>
            </div>

            {aiResponderEnabled ? (
              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-medium mb-3 text-[var(--t-text-muted)] uppercase tracking-wider">Responder Tone</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['Professional', 'Friendly', 'Custom'].map(t => (
                      <button
                        key={t}
                        onClick={() => setAiResponderTone(t)}
                        className={`py-2 px-3 rounded-xl text-xs font-semibold border transition-all ${
                          aiResponderTone === t 
                            ? 'bg-[var(--t-primary)] border-[var(--t-primary)] text-white shadow-lg' 
                            : 'bg-[var(--t-background)] border-[var(--t-border)] text-[var(--t-text-muted)] hover:border-[var(--t-primary-dim)]'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {aiResponderTone === 'Custom' && (
                  <div>
                    <label className="block text-xs font-medium mb-2 text-[var(--t-text-muted)]">Custom System Prompt</label>
                    <textarea
                      value={aiResponderPrompt}
                      onChange={(e) => setAiResponderPrompt(e.target.value)}
                      rows={4}
                      placeholder="e.g. Respond like a high-energy real estate investor named Jack..."
                      className="w-full rounded-xl px-4 py-3 bg-[var(--t-background)] border border-[var(--t-border)] text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all resize-none text-sm"
                    />
                  </div>
                )}

                <div className="space-y-4 pt-4 border-t border-[var(--t-border)]">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider">Advanced AI Rules</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between group">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-[var(--t-primary)]" />
                        <div>
                          <p className="text-xs font-medium text-white">Require Manual Approval</p>
                          <p className="text-[10px] text-[var(--t-text-muted)]">Draft every response as a notification first.</p>
                        </div>
                      </div>
                      <div 
                        onClick={() => setAiConfirmBeforeSending(!aiConfirmBeforeSending)}
                        className={`toggle-switch ${aiConfirmBeforeSending ? 'is-on' : 'is-off'}`}
                      >
                        <div className="toggle-knob" />
                      </div>
                    </div>

                    <div className="flex items-center justify-between group">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[var(--t-warning)]" />
                        <div>
                          <p className="text-xs font-medium text-white">Block Appointment Proposals</p>
                          <p className="text-[10px] text-[var(--t-text-muted)]">AI will never suggest specific dates or times.</p>
                        </div>
                      </div>
                      <div 
                        onClick={() => setAiNoScheduling(!aiNoScheduling)}
                        className={`toggle-switch ${aiNoScheduling ? 'is-on' : 'is-off'}`}
                      >
                        <div className="toggle-knob" />
                      </div>
                    </div>

                    <div className="flex items-center justify-between group">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-[var(--t-info)]" />
                        <div>
                          <p className="text-xs font-medium text-white">Explicit Approval Only (YES Reply)</p>
                          <p className="text-[10px] text-[var(--t-text-muted)]">Only respond if you reply "YES" to the draft.</p>
                        </div>
                      </div>
                      <div 
                        onClick={() => setAiExplicitOnly(!aiExplicitOnly)}
                        className={`toggle-switch ${aiExplicitOnly ? 'is-on' : 'is-off'}`}
                      >
                        <div className="toggle-knob" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-[var(--t-text-muted)] uppercase tracking-widest">Custom AI Instructions</label>
                    <textarea
                      value={aiCustomRules}
                      onChange={(e) => setAiCustomRules(e.target.value)}
                      rows={3}
                      placeholder="e.g. Always mention we are a family-owned business..."
                      className="w-full rounded-xl px-4 py-3 bg-[var(--t-background)] border border-[var(--t-border)] text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all resize-none text-xs"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center border-2 border-dashed border-[var(--t-border)] rounded-2xl">
                <p className="text-sm text-[var(--t-text-muted)]">Auto-responder is currently disabled.</p>
              </div>
            )}
          </div>

          <div className="bg-[var(--t-surface)] rounded-2xl border border-[var(--t-border)] p-6 space-y-4 shadow-xl">
            <h3 className="text-sm font-semibold text-white">Action Center</h3>
            {testResult && (
              <div className={`p-3 rounded-xl border text-xs flex items-center gap-2 ${testResult.success ? 'bg-[var(--t-success-dim)] border-[var(--t-success-border)] text-[var(--t-success)]' : 'bg-[var(--t-error-dim)] border-[var(--t-error-border)] text-[var(--t-error)]'}`}>
                {testResult.success ? <Check className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                {testResult.message}
              </div>
            )}
            {saveResult && (
              <div className={`p-3 rounded-xl border text-xs flex items-center gap-2 ${saveResult.success ? 'bg-[var(--t-success-dim)] border-[var(--t-success-border)] text-[var(--t-success)]' : 'bg-[var(--t-error-dim)] border-[var(--t-error-border)] text-[var(--t-error)]'}`}>
                {saveResult.success ? <Check className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                {saveResult.message}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleTestSMS}
                disabled={testing || !phone}
                className="flex items-center justify-center gap-2 py-3 rounded-xl bg-[var(--t-background)] border border-[var(--t-border)] text-white text-xs font-bold hover:bg-[var(--t-surface-hover)] disabled:opacity-50 transition-all"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                TEST CONNECTION
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center justify-center gap-2 py-3 rounded-xl bg-[var(--t-primary)] text-white text-xs font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-50 transition-all shadow-lg"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                SAVE ALL SETTINGS
              </button>
            </div>
          </div>
        </div>
      </div>

      {showTwilioWizard && (
        <SetupWizard 
          type="twilio" 
          onClose={() => setShowTwilioWizard(false)}
          onComplete={(data: TwilioSetupData | AISetupData) => {
            const twilioData = data as TwilioSetupData;
            setTwilioSid(twilioData.twilioSid);
            setTwilioToken(twilioData.twilioToken);
            if (twilioData.twilioPhone && !twilioNumbers.includes(twilioData.twilioPhone)) {
              setTwilioNumbers([twilioData.twilioPhone, ...twilioNumbers]);
            }
          }}
        />
      )}

      {showAIWizard && (
        <SetupWizard 
          type="ai" 
          onClose={() => setShowAIWizard(false)}
          onComplete={(data: TwilioSetupData | AISetupData) => {
            const aiData = data as AISetupData;
            // Here we might need to update a specific provider key in a different table,
            // but for now let's just show it's done. 
            // In a real app, you'd save data.apiKey to the appropriate setting.
            console.log('AI Provider Setup Complete:', aiData);
          }}
        />
      )}
    </div>
  );
}


